import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { accountingService } from '../services/accountingService';
import { NewExpense, ExpenseStatus } from '../types/accounting';
import { Project } from '../types/project';
import { projectService } from '../services/projectService';
import { useAuth } from '../hooks/UserContext';

interface AddExpenseModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const expenseTypes = [
  { value: 'VENDOR_PAYMENT', label: 'Vendor Payment' },
  { value: 'PURCHASE', label: 'Purchase' },
  { value: 'TRANSPORT', label: 'Transport' },
  { value: 'FOOD', label: 'Food' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'OFFICE', label: 'Office' },
  { value: 'DAILY_LABOR_BILL', label: 'Daily Labor Bill' },
  { value: 'CONSUMABLE', label: 'Consumable' },
];

const expenseStatus = [
  { value: ExpenseStatus.PAID, label: 'Paid' },
  { value: ExpenseStatus.PAYABLE, label: 'Payable' },
];

const AddExpenseModal: React.FC<AddExpenseModalProps> = ({ open, onClose, onSave }) => {
  const [newExpense, setNewExpense] = useState<Partial<NewExpense>>({ status: ExpenseStatus.PAID });
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (open && user) {
      const fetchProjects = async () => {
        setLoading(true);
        setError(null);
        try {
          const isFieldStaff = ['TECHNICIAN', 'ENGINEER', 'SUPERVISOR'].includes(user.role);
          
          // This now calls GET /projects/?mine=true
          const projectData = await projectService.getProjects({ 
            mine: isFieldStaff 
          });
          
          setProjects(projectData);
        } catch (err: any) {
          console.error('Failed to load projects:', err);
          setError('Could not load assigned projects. Please contact your manager.');
        } finally {
          setLoading(false);
        }
      };
      fetchProjects();
    }
  }, [open, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setNewExpense((prev: Partial<NewExpense>) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setNewExpense((prev: Partial<NewExpense>) => ({ ...prev, date: format(date, 'yyyy-MM-dd') }));
    }
  };

  const handleSubmit = async () => {
    if (!newExpense.project || !newExpense.amount || !newExpense.date || !newExpense.expense_type || !newExpense.status) {
      setError('Project, amount, date, expense type and status are required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await accountingService.createExpense(newExpense as NewExpense);
      onSave();
      onClose();
      setNewExpense({ status: ExpenseStatus.PAID }); // Reset form
    } catch (err: any) {
      console.error('Failed to create expense:', err);
      setError(err.response?.data?.detail || 'An unexpected error occurred.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New Expense</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading ? <CircularProgress /> : (
            <>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Project</InputLabel>
                <Select name="project" value={newExpense.project || ''} label="Project" onChange={handleChange}>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" required fullWidth label="Amount" name="amount" type="number" onChange={handleChange} />
              <DatePicker
                label="Date of Expense"
                value={newExpense.date ? new Date(newExpense.date) : null}
                onChange={handleDateChange}
                sx={{ width: '100%', mt: 2, mb: 1 }}
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Expense Type</InputLabel>
                <Select name="expense_type" value={newExpense.expense_type || ''} label="Expense Type" onChange={handleChange}>
                  {expenseTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={newExpense.status || ''} label="Status" onChange={handleChange}>
                  {expenseStatus.map((status) => (
                    <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>

                  ))}
                </Select>
              </FormControl>
              <TextField margin="normal" fullWidth label="Description" name="description" multiline rows={3} onChange={handleChange} />
              <TextField margin="normal" fullWidth label="Remark" name="remark" multiline rows={3} onChange={handleChange} />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving || loading}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddExpenseModal;